import { useCallback, useMemo } from 'react';
import { useMutation } from '@apollo/client';

import { ACTION_PRICE_CHANGE_REQUEST } from '../../../apollo/mutations/priceChangeRequest';
import {
    GET_AD_ZONE_STORES_FOR_REQUEST,
    GET_CATEGORY_STATS,
    GET_PRICE_CHANGE_REQUEST_BATCHES_TABLE_DATA,
    GET_PRICE_CHANGE_REQUEST_HISTORY,
    GET_PRICE_CHANGE_REQUEST_TABLE_ROW,
    GET_PRICE_CHANGE_REQUESTS_FOR_BATCH,
    GET_PRICE_CHANGE_REQUESTS_TABLE_DATA,
    GET_REQUEST_ESCALATION_MESSAGE,
    GET_SAP_OUTCOMES_FOR_REQUEST,
    GET_STATUS_COUNTS,
} from '../../../apollo/queries/priceChangeRequest';
import {
    GET_ACTION_CARD_ITEMS,
    GET_ACTIONS_FOR_REQUEST,
} from '../../../apollo/queries/priceChangeRequestActions';
import { Request } from '../../../codegen/graphql';
import { useAuthStore } from '../../../stores/auth.store';
import { useUser } from '../../../hooks/useUser';
import {
    showErrorNotification,
    showLoadingNotification,
    updateFailedNotification,
    updateSuccessNotification,
} from '../../../notifications';
import { getErrorMessage } from '../../../helpers/getErrorMessage';
import { PriceChangeRequestMetadata } from '../../../types/price-change-request';
import { resolveQuickActionSubmitCapability } from './resolvePriceChangeQuickActionTarget';

export const QUICK_ACTION_PRICE_CHANGE_REFETCH_QUERIES = [
    GET_ACTION_CARD_ITEMS,
    GET_ACTIONS_FOR_REQUEST,
    GET_STATUS_COUNTS,
    GET_CATEGORY_STATS,
    GET_PRICE_CHANGE_REQUESTS_TABLE_DATA,
    GET_PRICE_CHANGE_REQUEST_TABLE_ROW,
    GET_PRICE_CHANGE_REQUEST_HISTORY,
    GET_REQUEST_ESCALATION_MESSAGE,
    GET_AD_ZONE_STORES_FOR_REQUEST,
    GET_SAP_OUTCOMES_FOR_REQUEST,
    GET_PRICE_CHANGE_REQUEST_BATCHES_TABLE_DATA,
    GET_PRICE_CHANGE_REQUESTS_FOR_BATCH,
] as const;

export type QuickActionVerb = 'approve' | 'interim' | 'decline';

function getQuickActionNotificationLabels(verb: QuickActionVerb): {
    verbingTitle: string;
    verbedTitle: string;
    verbingMessage: string;
    verbedMessage: string;
} {
    if (verb === 'approve') {
        return {
            verbingTitle: 'Approving',
            verbedTitle: 'Approved',
            verbingMessage: 'Approving price change request.',
            verbedMessage: 'Price change request approved.',
        };
    }
    if (verb === 'interim') {
        return {
            verbingTitle: 'Suggesting interim price',
            verbedTitle: 'Interim price suggested',
            verbingMessage:
                'Accepting price change request with interim price.',
            verbedMessage: 'Price change request accepted with interim price.',
        };
    }
    return {
        verbingTitle: 'Declining',
        verbedTitle: 'Declined',
        verbingMessage: 'Declining price change request.',
        verbedMessage: 'Price change request declined.',
    };
}

type SubmitInput = {
    outcome: string;
    notes: string;
    metadata: PriceChangeRequestMetadata;
};

export type ResolvedQuickAction = {
    actionId: string;
    canSubmit: boolean;
};

export function useApplyQuickAction(request: Request, verb: QuickActionVerb) {
    const { isDivisionManager } = useUser();
    const { userId, standInForUserIds } = useAuthStore();

    const allResponsibleUserIds = useMemo(
        () => [userId, ...standInForUserIds].filter(Boolean) as string[],
        [userId, standInForUserIds],
    );

    const resolvedAction: ResolvedQuickAction | null = useMemo(() => {
        const { actionId, canSubmit } = resolveQuickActionSubmitCapability(
            request,
            verb,
            allResponsibleUserIds,
            isDivisionManager,
        );
        if (!actionId) {
            return null;
        }
        return { actionId, canSubmit };
    }, [
        request,
        verb,
        allResponsibleUserIds,
        isDivisionManager,
        request.priceChangeRequestActions,
    ]);

    const [actionPriceChangeRequest, { loading }] = useMutation(
        ACTION_PRICE_CHANGE_REQUEST,
        {
            refetchQueries: [...QUICK_ACTION_PRICE_CHANGE_REFETCH_QUERIES],
            awaitRefetchQueries: true,
        },
    );

    const submit = useCallback(
        async (input: SubmitInput): Promise<boolean> => {
            if (!resolvedAction) {
                showErrorNotification(
                    'No actionable price change request found.',
                );
                return false;
            }
            if (!resolvedAction.canSubmit) {
                showErrorNotification('Unable to submit request');
                return false;
            }

            const notificationId = `apply-quick-${verb}-${request.id}`;
            const { verbingTitle, verbedTitle, verbingMessage, verbedMessage } =
                getQuickActionNotificationLabels(verb);

            try {
                showLoadingNotification(
                    notificationId,
                    verbingTitle,
                    verbingMessage,
                );

                await actionPriceChangeRequest({
                    variables: {
                        input: {
                            outcome: input.outcome,
                            declinedReason: input.metadata.reason || null,
                            note: input.notes,
                            metadata: input.metadata,
                            actionId: resolvedAction.actionId,
                        },
                    },
                });

                updateSuccessNotification(
                    notificationId,
                    verbedTitle,
                    verbedMessage,
                );

                return true;
            } catch (error) {
                updateFailedNotification(
                    notificationId,
                    'Failed',
                    getErrorMessage(error),
                );
                return false;
            }
        },
        [actionPriceChangeRequest, resolvedAction, request.id, verb],
    );

    return {
        submit,
        loading,
        resolvedAction,
    };
}

export default useApplyQuickAction;
